"use client";

import React, { useState, useRef, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  useTheme,
  alpha,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  LocationOn as LocationIcon,
  Print as PrintIcon,
} from "@mui/icons-material";
import type { ScheduledItem, ScheduleItemCalendarView } from "./lib/types";
import { getScheduleItemViewBorderStyles } from "./schedular-styles";
import { ApplicationUser } from "components/QueueBoard/models/workOrderTypes";
import WorkOrderOpenIconButtons from "../common/WorkOrderOpen/WorkOrderOpenIconButtons";
import "./day-overview.css";
import { DAY_NAMES, formatHours } from "./lib/utils/schedular-utils";

// Performance tracking - render counter
let dayOverviewRenderCount = 0;

interface DayOverviewProps {
  referenceDate: Date;
  onReferenceDateChange?: (date: Date) => void;
  technicians: ApplicationUser[];
  scheduledItems: ScheduledItem[];
  locations: any[];
  getBaseCapacity: (techId: number, day: string, date?: Date) => number;
  getScheduledItemsForTechDay: (techId: number, day: string) => ScheduledItem[];
  onItemRemove?: (itemId: string) => void;
  onItemComplete?: (itemId: string) => Promise<void>;
  readOnly?: boolean;
  scheduleItemView?: ScheduleItemCalendarView;
  onDropZone?: (
    e: React.DragEvent,
    techId: number,
    day: string,
    date?: Date,
  ) => void;
  onDropAtPosition?: (
    e: React.DragEvent,
    techId: number,
    day: string,
    position: number,
    date?: Date,
  ) => void;
  onReorderItems?: (itemId: string, newSortKey: number) => void;
  workOrders: any[];
  segmentParts: any[];
  onOpenWorkOrder?: (invoiceDocId: number) => void;
}

export function DayOverview({
  referenceDate,
  technicians,
  scheduledItems,
  locations,
  getBaseCapacity,
  getScheduledItemsForTechDay,
  onDropZone,
  onDropAtPosition,
  onReorderItems,
  onItemRemove,
  onItemComplete,
  readOnly = false,
  scheduleItemView = "active",
  workOrders,
  segmentParts,
  onOpenWorkOrder,
}: DayOverviewProps) {
  // Performance tracking
  //dayOverviewRenderCount++;
  // console.log(`[DAY TAB] DayOverview render #${dayOverviewRenderCount}`, {
  //   referenceDate: referenceDate.toISOString(),
  //   techniciansCount: technicians.length,
  //   scheduledItemsCount: scheduledItems.length,
  // });

  const dayName = DAY_NAMES[referenceDate.getDay()];
  const dayOfMonth = referenceDate.getDate();
  const dayLabel = dayName.charAt(0).toUpperCase() + dayName.slice(1);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Technician Sections */}
      {technicians.map((tech) => {
        const techScheduledItems = getScheduledItemsForTechDay(
          tech.identity,
          dayName,
        );
        const techHours = techScheduledItems.reduce(
          (sum, item) => sum + item.hours,
          0,
        );
        const techCapacity = getBaseCapacity(
          tech.identity,
          dayName,
          referenceDate,
        );
        const utilization =
          techCapacity > 0 ? Math.round((techHours / techCapacity) * 100) : 0;

        return (
          <TechnicianSection
            key={tech.identity}
            technician={tech}
            scheduledItems={techScheduledItems}
            allScheduledItems={scheduledItems}
            loggedHours={techHours}
            capacity={techCapacity}
            utilization={utilization}
            dayName={dayName}
            dayLabel={dayLabel}
            dayOfMonth={dayOfMonth}
            referenceDate={referenceDate}
            locations={locations}
            onDropZone={onDropZone}
            onDropAtPosition={onDropAtPosition}
            onReorderItems={onReorderItems}
            onItemRemove={onItemRemove}
            readOnly={readOnly}
            scheduleItemView={scheduleItemView}
            onOpenWorkOrder={onOpenWorkOrder}
            workOrders={workOrders}
            segmentParts={segmentParts}
          />
        );
      })}
    </Box>
  );
}

// Technician Section Component - Table Layout
interface TechnicianSectionProps {
  technician: ApplicationUser;
  scheduledItems: ScheduledItem[];
  allScheduledItems: ScheduledItem[];
  loggedHours: number;
  capacity: number;
  utilization: number;
  dayName: string;
  dayLabel: string;
  dayOfMonth: number;
  referenceDate: Date;
  locations?: any[];
  onDropZone?: (
    e: React.DragEvent,
    techId: number,
    day: string,
    date?: Date,
  ) => void;
  onDropAtPosition?: (
    e: React.DragEvent,
    techId: number,
    day: string,
    position: number,
    date?: Date,
  ) => void;
  onReorderItems?: (itemId: string, newSortKey: number) => void;
  onItemRemove?: (itemId: string) => void;
  onItemComplete?: (itemId: string) => Promise<void>;
  readOnly?: boolean;
  scheduleItemView?: ScheduleItemCalendarView;
  onOpenWorkOrder?: (invoiceDocId: number) => void;
  workOrders?: any[];
  segmentParts?: any[];
}

function TechnicianSection({
  technician,
  scheduledItems,
  allScheduledItems,
  loggedHours,
  capacity,
  utilization,
  dayName,
  referenceDate,
  locations = [],
  onDropZone,
  onDropAtPosition,
  onReorderItems,
  onItemRemove,
  readOnly = false,
  scheduleItemView = "active",
  onOpenWorkOrder,
}: TechnicianSectionProps) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  // Handle print for this technician's section
  const handlePrint = useCallback(() => {
    if (!printRef.current) return;

    const printContent = printRef.current.innerHTML;
    const printWindow = window.open("", "_blank", "width=800,height=600");

    if (printWindow) {
      const dateStr = referenceDate.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${technician.userName} - Schedule for ${dateStr}</title>
            <style>
              * { box-sizing: border-box; }
              body { 
                font-family: Arial, sans-serif; 
                padding: 20px; 
                margin: 0;
                color: #333;
              }
              .print-header {
                background: #1976d2;
                color: white;
                padding: 15px 20px;
                border-radius: 8px 8px 0 0;
                margin-bottom: 0;
              }
              .print-header h1 {
                margin: 0 0 5px 0;
                font-size: 24px;
              }
              .print-header p {
                margin: 0;
                opacity: 0.9;
              }
              .print-stats {
                display: flex;
                gap: 30px;
                margin-top: 10px;
              }
              .print-stats span {
                font-weight: bold;
              }
              table { 
                width: 100%; 
                border-collapse: collapse; 
                margin-top: 0;
              }
              th, td { 
                border: 1px solid #ddd; 
                padding: 10px 12px; 
                text-align: left; 
              }
              th { 
                background: #f5f5f5; 
                font-weight: 600;
                color: #666;
              }
              tr:nth-child(even) { background: #fafafa; }
              .total-row {
                background: #f5f5f5 !important;
                font-weight: bold;
              }
              @media print {
                body { padding: 0; }
                .print-header { 
                  -webkit-print-color-adjust: exact; 
                  print-color-adjust: exact;
                }
                th { 
                  -webkit-print-color-adjust: exact; 
                  print-color-adjust: exact;
                }
              }
            </style>
          </head>
          <body>
            <div class="print-header">
              <h1>${technician.userName}</h1>
              <p>${dateStr}</p>
              <div class="print-stats">
                <span>Hours: ${loggedHours.toFixed(1)} / ${capacity.toFixed(1)}</span>
                <span>Utilization: ${utilization}%</span>
              </div>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Work Order</th>
                  <th>Segment</th>
                  <th>Customer</th>
                  <th>Description</th>
                  <th>Unit Model</th>
                  <th style="text-align: right;">Hours</th>
                </tr>
              </thead>
              <tbody>
                ${scheduledItems
                  .map(
                    (item) => `
                  <tr>
                    <td>${item.invoiceNumber || item.workOrderNumber || "-"}</td>
                    <td>${item.displayText || "Segment"}</td>
                    <td>${item.customerName || "-"}</td>
                    <td>${item.woDescription || "-"}</td>
                    <td>${item.unitModel || "-"}</td>
                    <td style="text-align: right;">${item.hours.toFixed(1)}h</td>
                  </tr>
                `,
                  )
                  .join("")}
                <tr class="total-row">
                  <td colspan="5">Total</td>
                  <td style="text-align: right;">${loggedHours.toFixed(1)}h</td>
                </tr>
              </tbody>
            </table>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }
  }, [
    technician,
    scheduledItems,
    loggedHours,
    capacity,
    utilization,
    referenceDate,
  ]);

  // Calculate sortKey for inserting an item at a specific position
  const calculateSortKey = (
    existingItems: ScheduledItem[],
    insertPosition: number,
  ): number => {
    if (existingItems.length === 0) {
      return 1000.0;
    }

    const sorted = [...existingItems].sort((a, b) => {
      const aKey = a.sortKey ?? a.order * 1000.0;
      const bKey = b.sortKey ?? b.order * 1000.0;
      return aKey - bKey;
    });

    if (insertPosition === 0) {
      const firstKey = sorted[0].sortKey ?? sorted[0].order * 1000.0;
      return firstKey - 1.0;
    } else if (insertPosition >= sorted.length) {
      const lastKey =
        sorted[sorted.length - 1].sortKey ??
        sorted[sorted.length - 1].order * 1000.0;
      return lastKey + 1.0;
    } else {
      const beforeKey =
        sorted[insertPosition - 1].sortKey ??
        sorted[insertPosition - 1].order * 1000.0;
      const afterKey =
        sorted[insertPosition].sortKey ?? sorted[insertPosition].order * 1000.0;
      return (beforeKey + afterKey) / 2.0;
    }
  };

  // Handle drag start for scheduled items
  const handleItemDragStart = (
    e: React.DragEvent,
    item: ScheduledItem,
    index: number,
  ) => {
    e.stopPropagation();
    setDraggedItemId(item.id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", index.toString());

    const dragData = {
      type: "scheduled-item" as const,
      id: item.id,
      hours: item.hours,
      workOrderId: item.workOrderId,
      scheduledItemId: item.id,
    };
    e.dataTransfer.setData("application/json", JSON.stringify(dragData));
  };

  // Handle drag end
  const handleItemDragEnd = () => {
    setDraggedItemId(null);
  };

  // Handle drop on a row (for reordering)
  const handleRowDrop = (e: React.DragEvent, toIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      const dragDataStr = e.dataTransfer.getData("application/json");
      if (!dragDataStr) return;

      const dragData = JSON.parse(dragDataStr);

      // Check if this is a scheduled item being moved
      if (dragData.type === "scheduled-item" && dragData.scheduledItemId) {
        const fromItemId = dragData.scheduledItemId;

        // Check if item is in this technician's section (reorder) or from another (move)
        const isItemInThisSection = scheduledItems.some(
          (item) => item.id === fromItemId,
        );

        if (isItemInThisSection && onReorderItems) {
          // Reorder within same technician
          const sortedItems = [...scheduledItems].sort((a, b) => {
            const aKey = a.sortKey ?? a.order * 1000.0;
            const bKey = b.sortKey ?? b.order * 1000.0;
            return aKey - bKey;
          });
          const itemsWithoutMoved = sortedItems.filter(
            (item) => item.id !== fromItemId,
          );
          const targetSortKey = calculateSortKey(itemsWithoutMoved, toIndex);
          onReorderItems(fromItemId, targetSortKey);
        } else if (onDropAtPosition) {
          // Move from another technician - use onDropAtPosition
          onDropAtPosition(
            e,
            technician.identity,
            dayName,
            toIndex,
            referenceDate,
          );
        }
        setDraggedItemId(null);
        return;
      }

      // For new items (work-order, segment, segment-part), use onDropAtPosition
      if (
        dragData.type === "work-order" ||
        dragData.type === "segment" ||
        dragData.type === "segment-part"
      ) {
        if (onDropAtPosition) {
          onDropAtPosition(
            e,
            technician.identity,
            dayName,
            toIndex,
            referenceDate,
          );
        }
        setDraggedItemId(null);
        return;
      }
    } catch (error) {
      console.error("Error handling drop:", error);
    }

    setDraggedItemId(null);
  };

  // Find technician's location name
  const technicianLocation = locations.find(
    (loc: any) => loc.identity === technician.defaultLocationIdentity,
  );
  const locationName = technicianLocation?.displayText || "";

  // Determine utilization color
  const getUtilizationColor = () => {
    if (utilization >= 100) return theme.palette.error.main;
    if (utilization >= 70) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  return (
    <Paper
      ref={printRef}
      elevation={1}
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "background.paper",
      }}
      onDrop={(e) => {
        e.preventDefault();
        if (onDropZone) {
          onDropZone(e, technician.identity, dayName, referenceDate);
        }
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          bgcolor: isDarkMode ? "#424242" : "#1976d2",
          p: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* Left Side - Technician Name & Location */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "white",
            }}
          >
            {technician.userName}
          </Typography>
          {/* Location indicator */}
          {locationName && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                bgcolor: alpha(theme.palette.common.white, 0.15),
                color: "white",
                py: 0.5,
                px: 1,
                borderRadius: 1,
                fontSize: "0.85rem",
              }}
            >
              <LocationIcon sx={{ fontSize: "1rem" }} />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                {locationName}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Right Side - Hours & Utilization */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="caption"
              sx={{
                color: alpha(theme.palette.common.white, 0.7),
                display: "block",
                mb: 0.5,
              }}
            >
              Hours
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "white", lineHeight: "28px" }}
            >
              {formatHours(loggedHours)}/{formatHours(capacity)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="caption"
              sx={{
                color: alpha(theme.palette.common.white, 0.7),
                display: "block",
                mb: 0.5,
              }}
            >
              Utilization
            </Typography>
            <Chip
              label={`${utilization}%`}
              size="small"
              sx={{
                bgcolor: getUtilizationColor(),
                color: "white",
                fontWeight: 700,
                fontSize: "0.8rem",
                height: 28,
                minWidth: 60,
                "& .MuiChip-label": {
                  px: 1,
                },
              }}
            />
          </Box>
          {/* Print Button - Only show if there are scheduled items */}
          {scheduledItems.length > 0 && (
            <Tooltip title="Print technician schedule" arrow>
              <IconButton
                onClick={handlePrint}
                size="small"
                sx={{
                  color: "white",
                  bgcolor: alpha(theme.palette.common.white, 0.15),
                  ml: 1,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.common.white, 0.25),
                  },
                }}
              >
                <PrintIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Utilization Progress Bar */}
      <LinearProgress
        variant="determinate"
        value={Math.min(utilization, 100)}
        sx={{
          height: 4,
          bgcolor: alpha(theme.palette.action.disabled, 0.3),
          "& .MuiLinearProgress-bar": {
            bgcolor: getUtilizationColor(),
          },
        }}
      />

      {/* Table Section */}
      {scheduledItems.length > 0 ? (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: isDarkMode
                    ? alpha(theme.palette.grey[800], 0.5)
                    : alpha(theme.palette.grey[100], 1),
                }}
              >
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    fontSize: "0.8rem",
                    py: 1.5,
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  Work Order
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    fontSize: "0.8rem",
                    py: 1.5,
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  Segment
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    fontSize: "0.8rem",
                    py: 1.5,
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  Customer
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    fontSize: "0.8rem",
                    py: 1.5,
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  Description
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    fontSize: "0.8rem",
                    py: 1.5,
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  Unit Model
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    fontSize: "0.8rem",
                    py: 1.5,
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  Hours
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    fontSize: "0.8rem",
                    py: 1.5,
                    borderBottom: 1,
                    borderColor: "divider",
                    width: 50,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scheduledItems.map((item, index) => (
                <TableRow
                  key={item.id}
                  draggable={!readOnly}
                  onDragStart={
                    readOnly
                      ? undefined
                      : (e) => handleItemDragStart(e, item, index)
                  }
                  onDragEnd={handleItemDragEnd}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                  }}
                  onDrop={(e) => handleRowDrop(e, index)}
                  onClick={() => {
                    const invoiceDocId = parseInt(item.workOrderId, 10);
                    if (isNaN(invoiceDocId)) return;

                    onOpenWorkOrder?.(invoiceDocId);
                  }}
                  sx={{
                    cursor: readOnly
                      ? "pointer"
                      : draggedItemId === item.id
                        ? "grabbing"
                        : "grab",
                    transition: "all 0.2s ease",
                    bgcolor:
                      draggedItemId === item.id
                        ? alpha(theme.palette.primary.main, 0.2)
                        : scheduleItemView === "completed"
                          ? alpha(theme.palette.success.main, 0.08)
                          : scheduleItemView === "archived"
                            ? alpha(theme.palette.error.main, 0.08)
                            : "transparent",
                    opacity: draggedItemId === item.id ? 0.7 : 1,
                    transform:
                      draggedItemId === item.id ? "scale(1.02)" : "scale(1)",
                    ...(scheduleItemView !== "active"
                      ? getScheduleItemViewBorderStyles(theme, scheduleItemView)
                      : {}),
                    "&:hover": {
                      bgcolor:
                        draggedItemId === item.id
                          ? alpha(theme.palette.primary.main, 0.2)
                          : scheduleItemView === "completed"
                            ? alpha(theme.palette.success.main, 0.12)
                            : scheduleItemView === "archived"
                              ? alpha(theme.palette.error.main, 0.12)
                              : isDarkMode
                                ? alpha(theme.palette.primary.main, 0.1)
                                : alpha(theme.palette.primary.main, 0.05),
                    },
                    "&:last-child td": {
                      borderBottom: 0,
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      py: 1.5,
                      borderBottom: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "primary.main",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                      }}
                    >
                      {item.invoiceNumber || item.workOrderNumber}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1.5,
                      borderBottom: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.primary",
                        fontSize: "0.85rem",
                      }}
                    >
                      {item.displayText || "Segment"}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1.5,
                      borderBottom: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.85rem",
                      }}
                    >
                      {item.customerName || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1.5,
                      borderBottom: 1,
                      borderColor: "divider",
                      maxWidth: 200,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.85rem",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={item.woDescription || "-"}
                    >
                      {item.woDescription || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1.5,
                      borderBottom: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.85rem",
                      }}
                    >
                      {item.unitModel || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      py: 1.5,
                      borderBottom: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "primary.main",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                      }}
                    >
                      {formatHours(item.hours)}h
                    </Typography>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      py: 1.5,
                      borderBottom: 1,
                      borderColor: "divider",
                      width: 50,
                    }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.25,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <WorkOrderOpenIconButtons
                        invoiceDocId={parseInt(item.workOrderId, 10)}
                        disabled={isNaN(parseInt(item.workOrderId, 10))}
                        onOpenInServiceQueue={(invoiceDocId) =>
                          onOpenWorkOrder?.(invoiceDocId)
                        }
                        iconFontSize="0.875rem"
                      />
                      {!readOnly && (
                      <Tooltip title="Mark Archive" arrow placement="top">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onItemRemove?.(item.id);
                        }}
                        sx={{
                          color: "text.secondary",
                          "&:hover": {
                            color: "error.main",
                            bgcolor: alpha(theme.palette.error.main, 0.1),
                          },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {/* Drop zone at the end for adding items at the bottom */}
              <TableRow
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                }}
                onDrop={(e) => handleRowDrop(e, scheduledItems.length)}
                sx={{
                  height: 40,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                  },
                }}
              >
                <TableCell
                  colSpan={7}
                  sx={{
                    borderBottom: 0,
                    color: "text.secondary",
                    fontStyle: "italic",
                    fontSize: "0.8rem",
                    textAlign: "center",
                  }}
                >
                  Drop here to add at the end
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box
          sx={{
            p: 3,
            textAlign: "center",
            color: "text.secondary",
            minHeight: 80,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: `2px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
            borderRadius: 1,
            mx: 2,
            my: 2,
            transition: "all 0.2s ease",
            "&:hover": {
              borderColor: alpha(theme.palette.primary.main, 0.4),
              bgcolor: alpha(theme.palette.primary.main, 0.02),
            },
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            try {
              const dragDataStr = e.dataTransfer.getData("application/json");
              if (!dragDataStr) {
                // Fallback to onDropZone for basic drops
                if (onDropZone) {
                  onDropZone(e, technician.identity, dayName, referenceDate);
                }
                return;
              }

              const dragData = JSON.parse(dragDataStr);

              // Use onDropAtPosition for all item types
              if (onDropAtPosition) {
                onDropAtPosition(
                  e,
                  technician.identity,
                  dayName,
                  0,
                  referenceDate,
                );
              } else if (onDropZone) {
                onDropZone(e, technician.identity, dayName, referenceDate);
              }
            } catch (error) {
              console.error("Error handling empty zone drop:", error);
              if (onDropZone) {
                onDropZone(e, technician.identity, dayName, referenceDate);
              }
            }
          }}
        >
          <Typography variant="body2" sx={{ fontStyle: "italic" }}>
            Drop work orders or segments here
          </Typography>
        </Box>
      )}

      {/* Footer - Total */}
      {scheduledItems.length > 0 && (
        <Box
          sx={{
            p: 1.5,
            borderTop: 1,
            borderColor: "divider",
            display: "flex",
            justifyContent: "flex-end",
            bgcolor: isDarkMode
              ? alpha(theme.palette.grey[800], 0.3)
              : alpha(theme.palette.grey[50], 1),
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            Total: {formatHours(loggedHours)}h
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
